/* eslint-disable react/prop-types */
import { Box, Typography } from '@mui/material';
import { Surface, TextInput } from 'components/ui';
import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { UpdateApprovalFlowOrderRequest, ApproverType, GetSchoolResponse } from 'services/api';

export interface ApprovalFlowOrderProps {
  schoolId: string;
  isMultiStepApprovalEnabled: boolean;
  isSequentialApprovalEnabled: boolean;
  schoolData?: GetSchoolResponse | null;
}

export interface ApprovalFlowOrderRef {
  getOrderValues: () => UpdateApprovalFlowOrderRequest | null;
  validate: () => string | null;
}

const APPROVER_TYPE_LABELS: Record<ApproverType, string> = {
  [ApproverType.ORG_ADMIN]: 'Organization Admin',
  [ApproverType.FACILITY_APPROVERS]: 'Facility Approvers',
  [ApproverType.CATEGORY_APPROVERS]: 'Category Approvers',
  [ApproverType.SEQUENCE_APPROVERS]: 'Sequence Approvers',
};

export const ApprovalFlowOrderTab = forwardRef<ApprovalFlowOrderRef, ApprovalFlowOrderProps>(({
  schoolId: _schoolId,
  isMultiStepApprovalEnabled,
  isSequentialApprovalEnabled,
  schoolData,
}, ref) => {
  const [orderValues, setOrderValues] = useState<Record<ApproverType, number | ''>>({
    [ApproverType.ORG_ADMIN]: 1,
    [ApproverType.FACILITY_APPROVERS]: 2,
    [ApproverType.CATEGORY_APPROVERS]: 3,
    [ApproverType.SEQUENCE_APPROVERS]: 4,
  });

  useEffect(() => {
    if (!schoolData?.approvalFlowOrder) {
      // Set defaults if no order exists
      setOrderValues({
        [ApproverType.ORG_ADMIN]: 1,
        [ApproverType.FACILITY_APPROVERS]: 2,
        [ApproverType.CATEGORY_APPROVERS]: 3,
        [ApproverType.SEQUENCE_APPROVERS]: 4,
      });
      return;
    }

    const newOrderValues: Record<ApproverType, number | ''> = {
      [ApproverType.ORG_ADMIN]: 1,
      [ApproverType.FACILITY_APPROVERS]: 2,
      [ApproverType.CATEGORY_APPROVERS]: 3,
      [ApproverType.SEQUENCE_APPROVERS]: 4,
    };

    schoolData.approvalFlowOrder.items.forEach(item => {
      newOrderValues[item.approverType] = item.order;
    });

    setOrderValues(newOrderValues);
  }, [schoolData?.approvalFlowOrder]);

  const handleOrderChange = useCallback((approverType: ApproverType, value: string) => {
    // Allow empty string for clearing the value
    if (value === '') {
      setOrderValues(prev => ({
        ...prev,
        [approverType]: '',
      }));
      return;
    }

    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;

    setOrderValues(prev => ({
      ...prev,
      [approverType]: numValue,
    }));
  }, []);

  useImperativeHandle(ref, () => ({
    getOrderValues: () => {
      if (!isMultiStepApprovalEnabled) return null;

      const items = Object.entries(orderValues)
        .filter(([type]) => {
          // Only include SEQUENCE_APPROVERS if sequential approval is enabled
          if (type === ApproverType.SEQUENCE_APPROVERS)
            return isSequentialApprovalEnabled;

          return true;
        })
        .map(([approverType, order]) => {
          if (order === '' || order < 1)
            throw new Error(`Order value is required and must be at least 1 for ${approverType}`);

          return {
            approverType: approverType as ApproverType,
            order: order,
          };
        });

      return { items };
    },
    validate: () => {
      if (!isMultiStepApprovalEnabled) return null;

      const approverTypes = [
        ApproverType.ORG_ADMIN,
        ApproverType.FACILITY_APPROVERS,
        ApproverType.CATEGORY_APPROVERS,
        ...(isSequentialApprovalEnabled ? [ApproverType.SEQUENCE_APPROVERS] : []),
      ];

      for (const approverType of approverTypes) {
        const order = orderValues[approverType];
        if (order === '' || (typeof order === 'number' && order < 1))
          return `Order value is required and must be at least 1 for ${APPROVER_TYPE_LABELS[approverType]}`;

      }

      return null;
    },
  }), [orderValues, isMultiStepApprovalEnabled, isSequentialApprovalEnabled]);

  if (!isMultiStepApprovalEnabled)
    return null;


  const approverTypes = [
    ApproverType.ORG_ADMIN,
    ApproverType.FACILITY_APPROVERS,
    ApproverType.CATEGORY_APPROVERS,
    ...(isSequentialApprovalEnabled ? [ApproverType.SEQUENCE_APPROVERS] : []),
  ];

  return (
    <Surface mt={20} mr={18} noInnerPadding>
      <Box p={20}>
        <Typography sx={{ fontSize: '1.125em', fontWeight: 600, mb: 20 }}>
          Approval Flow Order
        </Typography>
        <Typography sx={{ fontSize: '0.875em', color: 'text.secondary', mb: 30 }}>
          Set the order in which different approver types need to approve events. Lower numbers are approved first.
          Multiple approver types can have the same order number (they will approve in parallel).
        </Typography>

        <Box display="flex" flexDirection="column" gap={20}>
          {approverTypes.map(approverType => (
            <Box
              key={approverType}
              display="flex"
              alignItems="center"
              gap={20}
              p={15}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box minWidth={200}>
                <Typography sx={{ fontSize: '0.875em', fontWeight: 500 }}>
                  {APPROVER_TYPE_LABELS[approverType]}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={10}>
                <Typography sx={{ fontSize: '0.875em' }}>Order:</Typography>
                <Box sx={{ width: 80 }}>
                  <TextInput
                    type="number"
                    min={1}
                    value={orderValues[approverType] === '' ? '' : orderValues[approverType]}
                    onChange={e => handleOrderChange(approverType, e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Surface>
  );
});

